// IndexedDB outbox — the offline half of the save-run flow. A "quick run"
// composed with the network off is written here as a run.created sync event
// (same shape POST /api/sync/push expects — see platform.ts's processPush)
// instead of going straight to POST /api/runs. sync.ts drains this store on
// reconnect. Modelled on legacy static/js/sync.js's tx()/openDb() pattern —
// same request-then-transaction-completes ordering guarantee, ported to TS.

const DB_NAME = "trimlog-v5";
const DB_VERSION = 2;
const STORE_OUTBOX = "outbox";
const STORE_RUNS_CACHE = "runsCache";
const STORE_PHOTO_OUTBOX = "photoOutbox";

export type SyncEvent = {
  event_id: string;
  type: string;
  entity_type: string;
  entity_id: string;
  actor_player_id: number;
  device_id: string;
  occurred_at: number;
  payload: Record<string, unknown>;
  queued_at: number;
  attempts: number;
  last_error: string | null;
};

export type CachedRun = {
  client_uuid: string;
  player_name: string;
  strain_name: string | null;
  end_grams: number | null;
  gpm: number | null;
  mode: string;
  created_at: number;
  pending: boolean;
};

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_OUTBOX)) {
        db.createObjectStore(STORE_OUTBOX, { keyPath: "event_id" });
      }
      if (!db.objectStoreNames.contains(STORE_RUNS_CACHE)) {
        db.createObjectStore(STORE_RUNS_CACHE, { keyPath: "client_uuid" });
      }
      if (!db.objectStoreNames.contains(STORE_PHOTO_OUTBOX)) {
        db.createObjectStore(STORE_PHOTO_OUTBOX, { keyPath: "photo_id" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

function tx<T>(store: string, mode: IDBTransactionMode, fn: (s: IDBObjectStore) => Promise<T> | void): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const t = db.transaction(store, mode);
        const s = t.objectStore(store);
        const out = fn(s);
        t.oncomplete = () => resolve(out as T);
        t.onerror = () => reject(t.error);
        t.onabort = () => reject(t.error);
      }),
  );
}

function reqPromise<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function enqueueEvent(ev: Omit<SyncEvent, "queued_at" | "attempts" | "last_error">): Promise<void> {
  await tx(STORE_OUTBOX, "readwrite", (s) => {
    s.put({ ...ev, queued_at: Date.now(), attempts: 0, last_error: null });
  });
}

export async function listEvents(): Promise<SyncEvent[]> {
  return tx(STORE_OUTBOX, "readonly", (s) => reqPromise(s.getAll() as IDBRequest<SyncEvent[]>));
}

export async function removeEvent(eventId: string): Promise<void> {
  await tx(STORE_OUTBOX, "readwrite", (s) => {
    s.delete(eventId);
  });
}

export async function markEventError(eventId: string, message: string): Promise<void> {
  await tx(STORE_OUTBOX, "readwrite", (s) =>
    reqPromise(s.get(eventId) as IDBRequest<SyncEvent | undefined>).then((cur) => {
      if (cur) {
        cur.attempts += 1;
        cur.last_error = message.slice(0, 160);
        s.put(cur);
      }
    }));
}

export async function cacheRun(run: CachedRun): Promise<void> {
  await tx(STORE_RUNS_CACHE, "readwrite", (s) => {
    s.put(run);
  });
}

// Once synced, GET /api/runs already carries the row — drop the local
// optimistic copy rather than flip a "pending" flag, so the home screen's
// recent-runs merge (cached-pending + server list) never double-lists it.
export async function markRunSynced(clientUuid: string): Promise<void> {
  await tx(STORE_RUNS_CACHE, "readwrite", (s) => {
    s.delete(clientUuid);
  });
}

export async function listCachedRuns(): Promise<CachedRun[]> {
  const rows = await tx(STORE_RUNS_CACHE, "readonly", (s) => reqPromise(s.getAll() as IDBRequest<CachedRun[]>));
  return rows.sort((a, b) => b.created_at - a.created_at);
}

// Photo outbox — full-batch start/end/QA photos captured offline (or whose
// online PUT attempt failed). Raw Blob storage: IndexedDB structured-clones
// Blob natively, so there's no need for the base64 round-trip the API side
// uses for its own on-row storage (see schema.ts's photos table comment).
export type QueuedPhoto = {
  photo_id: string;
  purpose: string;
  content_type: string;
  data: Blob;
  device_id: string;
  queued_at: number;
  attempts: number;
  last_error: string | null;
};

export async function enqueuePhoto(p: Omit<QueuedPhoto, "queued_at" | "attempts" | "last_error">): Promise<void> {
  await tx(STORE_PHOTO_OUTBOX, "readwrite", (s) => {
    s.put({ ...p, queued_at: Date.now(), attempts: 0, last_error: null });
  });
}

export async function listQueuedPhotos(): Promise<QueuedPhoto[]> {
  return tx(STORE_PHOTO_OUTBOX, "readonly", (s) => reqPromise(s.getAll() as IDBRequest<QueuedPhoto[]>));
}

export async function removeQueuedPhoto(photoId: string): Promise<void> {
  await tx(STORE_PHOTO_OUTBOX, "readwrite", (s) => {
    s.delete(photoId);
  });
}

export async function markPhotoError(photoId: string, message: string): Promise<void> {
  await tx(STORE_PHOTO_OUTBOX, "readwrite", (s) =>
    reqPromise(s.get(photoId) as IDBRequest<QueuedPhoto | undefined>).then((cur) => {
      if (cur) {
        cur.attempts += 1;
        cur.last_error = message.slice(0, 160);
        s.put(cur);
      }
    }));
}
